import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  incrementItemCount,
  decrementItemCount,
} from "../../redux/reducers/cart";
import { CART } from "../../rest/endpoints";
import { apiRequest } from "../../utils/apiRequest";
import { useAuthHeaders } from "../../utils/getHeaders";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";

const ResponsiveItemCard = ({ item }) => {
  const nameRef = useRef(null);
  const [descClamp, setDescClamp] = useState(1);

  useEffect(() => {
    if (nameRef.current) {
      const lineHeight = parseFloat(
        getComputedStyle(nameRef.current).lineHeight
      );
      const height = nameRef.current.offsetHeight;
      const nameLines = Math.round(height / lineHeight);
      const remaining = Math.max(4 - nameLines, 1);
      setDescClamp(remaining);
    }
  }, [item.name]);

  return (
    <div className="flex flex-col gap-1 w-[65%] pr-4">
      <h2
        ref={nameRef}
        className="text-base font-bold text-custom-maroon leading-snug overflow-hidden line-clamp-3"
      >
        {item.name}
      </h2>
      <p
        className={`text-sm text-gray-600 overflow-hidden line-clamp-${descClamp}`}
      >
        {item.description}
      </p>
      <p className="text-lg font-semibold text-custom-maroon mt-1">
        ₹{item.amount}
      </p>
    </div>
  );
};

const CartCard = ({ items, chadawaId }) => {
  const headers = useAuthHeaders();
  const [alreadyInCart, setAlreadyInCart] = useState({});

  const token = localStorage.getItem("userToken");

  const isLoggedIn = () => {
    return !!token;
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Function to open modal
  const openItemModal = (item, imageInfo) => {
    setSelectedItem({ ...item, imageInfo });
    setIsModalOpen(true);
  };

  // Function to handle adding from modal
  const handleAddFromModal = () => {
    if (selectedItem) {
      handleIncrement(
        selectedItem.id,
        selectedItem.amount,
        selectedItem.name,
        selectedItem.imageInfo
      );
    }
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (isLoggedIn()) {
      const getcartItems = async () => {
        try {
          const result = await apiRequest({
            url: CART,
            method: "GET",
            headers,
          });
          if (result?.data?.type === "PUJA") {
            console.log("No items in chadawa cart");
          } else if (result?.data?.type === "OFFERING") {
            setAlreadyInCart(result.data);
            if (alreadyInCart?.itemList?.length > 0) {
              alreadyInCart.itemList.forEach((item) => {
                dispatch(
                  incrementItemCount({
                    id: item.itemId,
                    amount: item.itemAmount,
                    itemName: item.name,
                    imageInfo: item.imageInfo,
                    chadawaId: alreadyInCart.parentId,
                  })
                );
              });
            }
          }
        } catch (error) {
          console.error(
            "Error fetching data:",
            error.message || "Unknown error"
          );
          toast.error(error.message || "Failed to fetch cart data.");
        }
      };

      getcartItems();
    }
  }, [token]);

  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.cartItems);

  const handleIncrement = async (id, amount, itemName, imageInfo) => {
    try {
      const result = await apiRequest({
        url: CART,
        method: "POST",
        data: {
          type: "OFFERING",
          parentId: chadawaId,
          subType: "OFFERING_ITEM",
          itemId: id,
          quantity: getItemCount(id) + 1,
        },
        headers,
      });

      if (!result.showModal) {
        dispatch(
          incrementItemCount({ id, amount, itemName, imageInfo, chadawaId })
        );
      }
    } catch (error) {
      console.error("Network or unknown error:", error.message);
      toast.error("Failed to update cart. Please try again.");
    }
  };

  const handleDecrement = async (id, amount, itemName, imageInfo) => {
    try {
      const result = await apiRequest({
        url: CART,
        method: "POST",
        data: {
          type: "OFFERING",
          parentId: chadawaId,
          subType: "OFFERING_ITEM",
          itemId: id,
          quantity: Math.max(0, getItemCount(id) - 1), // Prevents negative quantity
        },
        headers,
      });

      if (!result.showModal) {
        dispatch(
          decrementItemCount({ id, amount, itemName, imageInfo, chadawaId })
        );
      }
    } catch (error) {
      console.error("Network or unknown error:", error.message);
      toast.error("Failed to update cart. Please try again.");
    }
  };

  const getItemCount = (id) => {
    const item = cartItems.find(
      (item) => item.id === id && item.chadawaId === chadawaId
    );
    return item ? item.count : 0;
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-0 sm:gap-4">
        {items.map((item) => {
          const count = getItemCount(item.id);
          const imageInfo = item.imageInfo || {};

          return (
            <div key={item.id}>
              {/* Small screen (mobile) view - Vertical stacked card style like image */}
              <div className="bg-[#fff6ee] p-6 -mx-10 shadow-sm border-b-[1.5px] pb-10 border-dashed border-gray-500 sm:hidden min-h-[180px]">
                <div className="flex justify-between items-start">
                  {/* Left: Textual Content */}
                  <ResponsiveItemCard
                    key={item.id}
                    item={item}
                    count={getItemCount(item.id)}
                  />

                  {/* Right: Image and Button */}
                  <div className="relative flex flex-col items-center gap-2">
                    <div className="w-32 h-32 rounded-lg overflow-hidden">
                      <img
                        src={imageInfo.url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Quantity Control */}
                    <div className="absolute -bottom-4 w-24 bg-custom-beige rounded-full">
                      {count === 0 ? (
                        <button
                          className="w-full text-sm text-custom-orange font-semibold border-2 border-custom-orange rounded-full py-1 hover:bg-custom-orange hover:text-white transition"
                          onClick={() => openItemModal(item, imageInfo)}
                        >
                          + Add
                        </button>
                      ) : (
                        <div className="flex items-center justify-between border-2 border-custom-orange rounded-full px-2 py-1">
                          <button
                            className="text-custom-orange font-bold hover:bg-custom-orange hover:text-white w-6 h-6 rounded-full flex items-center justify-center transition"
                            onClick={() =>
                              handleDecrement(
                                item.id,
                                item.amount,
                                item.name,
                                imageInfo
                              )
                            }
                          >
                            -
                          </button>
                          <span className="text-sm font-semibold">{count}</span>
                          <button
                            className="text-custom-orange font-bold hover:bg-custom-orange hover:text-white w-6 h-6 rounded-full flex items-center justify-center transition"
                            onClick={() =>
                              handleIncrement(
                                item.id,
                                item.amount,
                                item.name,
                                imageInfo
                              )
                            }
                          >
                            +
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Medium and larger screens - Grid card */}
              <div className="bg-custom-beige rounded-[24px] shadow-sm hidden sm:block mb-4 h-[420px]">
                <div className="hidden sm:flex sm:flex-col h-full">
                  {/* Image section - keep fixed aspect ratio */}
                  <div className="rounded-[24px] overflow-hidden w-full">
                    <img
                      src={imageInfo.url}
                      alt={item.name}
                      className="w-full object-cover aspect-[5/4]"
                    />
                  </div>

                  {/* Content section */}
                  <div className="flex flex-col justify-between px-4 pb-4 flex-grow">
                    <div className="mt-4">
                      <h2
                        className="text-lg font-bold text-custom-maroon line-clamp-2"
                        style={{ minHeight: "3.4rem" }}
                      >
                        {item.name}
                      </h2>
                      <p className="text-sm text-custom-gray line-clamp-3">
                        {item.description}
                      </p>
                    </div>

                    {/* Price and Controls */}
                    <div className="mt-4">
                      <p className="text-2xl font-bold text-custom-maroon mr-2">
                        ₹{item.amount}
                      </p>
                      {count === 0 ? (
                        <button
                          className="w-full py-2 mt-4 text-custom-orange font-semibold border-2 border-custom-orange rounded-full hover:bg-custom-orange hover:text-white transition"
                          onClick={() =>
                            handleIncrement(
                              item.id,
                              item.amount,
                              item.name,
                              imageInfo
                            )
                          }
                        >
                          + Add
                        </button>
                      ) : (
                        <div className="flex items-center justify-between mt-4 border-2 border-custom-orange rounded-full py-2">
                          <div className="pl-2">
                            <button
                              className="text-custom-orange font-semibold hover:text-white hover:bg-custom-orange w-7 h-7 rounded-full transition-all flex items-center justify-center"
                              onClick={() =>
                                handleDecrement(
                                  item.id,
                                  item.amount,
                                  item.name,
                                  imageInfo
                                )
                              }
                            >
                              -
                            </button>
                          </div>
                          <span className="font-semibold">{count}</span>
                          <div className="pr-2">
                            <button
                              className="text-custom-orange font-semibold hover:text-white hover:bg-custom-orange w-7 h-7 rounded-full transition-all flex items-center justify-center"
                              onClick={() =>
                                handleIncrement(
                                  item.id,
                                  item.amount,
                                  item.name,
                                  imageInfo
                                )
                              }
                            >
                              +
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex flex-col justify-end z-50 animate-fadeIn">
          <div className="bg-custom-beige w-full rounded-t-xl pb-6 max-h-[90vh] overflow-y-auto animate-slideUp">
            {selectedItem?.imageInfo && (
              <div className="relative mb-4">
                <img
                  src={selectedItem?.imageInfo?.url}
                  alt={selectedItem.name}
                  className="w-full object-cover rounded-md"
                />
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="absolute h-8 w-8 top-4 right-4 bg-custom-maroon text-white rounded-full p-1 shadow-md"
                >
                  <CloseIcon fontSize="small" />
                </button>
              </div>
            )}

            <div className="px-6">
              <div className="mb-4 flex flex-row items-start justify-between">
                <div className="w-[65vw]">
                  <h3 className="text-lg font-semibold text-custom-maroon">
                    {selectedItem?.name}
                  </h3>
                  <p className="text-custom-gray">
                    {selectedItem?.description}
                  </p>
                </div>
                <p className="text-custom-maroon font-bold">
                  ₹{selectedItem?.amount}
                </p>
              </div>

              <button
                className="w-full bg-custom-orange text-white font-semibold py-2 rounded-full"
                onClick={handleAddFromModal}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartCard;
